/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAuth } from "../contexts/AuthContext";
import { usePageHeader } from "../contexts/PageHeaderContext";
import BreadCrumb from "../components/common/BreadCrumb";
import { toast } from "react-hot-toast";
import { changePassword } from "../api/user";
import UpgradePlansModal from "../components/UpgradePlansModal";
import PaymentFailureModal from "../components/PaymentFailureModal";
import PasswordInput from "../components/PasswordInput";
import Signature from "./Signature";
import { UserCircleIcon, KeyIcon } from "@heroicons/react/24/outline";
import CompanyAvatar from "../components/CompanyAvatar";
import { Input, Button } from "../components/ui";

const PROFILE_TABS = {
  general: "general",
  password: "password",
  signature: "signature",
};

const Profile = () => {
  const { user, organization, activeCompany, updateProfile, logout } =
    useAuth();
  const { setHeaderContent } = usePageHeader();
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);

  useEffect(() => {
    setHeaderContent(
      <div>
        <BreadCrumb
          items={[
            { label: "Dashboard", href: "/dashboard" },
            { label: "Profile Settings" },
          ]}
          className="mb-2"
        />
        <h1 className="font-heading text-xl sm:text-2xl font-bold text-gray-900">
          Profile Settings
        </h1>
        <p className="text-sm text-gray-600 max-w-2xl mt-1">
          Manage your profile and account details
        </p>
      </div>,
    );
    return () => setHeaderContent(null);
  }, [setHeaderContent]);
  const [paymentFailureModalOpen, setPaymentFailureModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(PROFILE_TABS.general);

  const formik = useFormik({
    initialValues: {
      name: user?.name || "",
      email: user?.email || "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      name: Yup.string(),
      email: Yup.string().email(),
    }),
    onSubmit: async (values) => {
      try {
        await updateProfile({ name: values.name, email: values.email });
        toast.success("Profile updated successfully");
      } catch (error) {
        toast.error(error.message || "Failed to update profile");
      }
    },
  });

  const passwordFormik = useFormik({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmNewPassword: "",
    },
    validationSchema: Yup.object({
      currentPassword: Yup.string().required("Current password is required"),
      newPassword: Yup.string()
        .min(6, "Password must be at least 6 characters")
        .required("New password is required"),
      confirmNewPassword: Yup.string()
        .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
        .required("Confirm password is required"),
    }),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        await changePassword(values.currentPassword, values.newPassword);
        toast.success("Password changed successfully. Please login again.");
        resetForm();
        setTimeout(() => {
          logout();
        }, 2000);
      } catch (error) {
        toast.error(error.message || "Failed to change password");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const currentPlan = organization?.planId;
  const subscriptionType = organization?.subscriptionType;
  const memberCount = organization?.memberCount;

  let billingCycle = subscriptionType?.toLowerCase() || null;
  let currentPrice = null;
  let totalPrice = null;

  if (currentPlan && subscriptionType) {
    if (subscriptionType === "MONTHLY") {
      currentPrice = currentPlan.monthlyPricePerMember;
      totalPrice = currentPrice * memberCount;
    } else if (subscriptionType === "YEARLY") {
      currentPrice = currentPlan.yearlyPricePerMember;
      totalPrice = currentPrice * memberCount;
    }
  }

  const inputBase =
    "block w-full px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 rounded-xl border border-gray-300 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors";
  const inputError =
    "border-red-500 focus:ring-red-500/20 focus:border-red-500";

  const sidebarLinks = [
    {
      value: PROFILE_TABS.general,
      label: "General Profile",
      icon: UserCircleIcon,
    },
    { value: PROFILE_TABS.password, label: "Update Password", icon: KeyIcon },
    // { value: PROFILE_TABS.signature, label: "Signature", icon: PencilSquareIcon },
  ];

  return (
    <div className="flex flex-col lg:flex-row gap-6 h-screen">
      {/* ── Vertical sidebar: Navigation links ── */}
      <aside className="w-full lg:w-60 shrink-0 lg:border-r px-4 py-4 border-gray-200 lg:rounded-none h-screen">
        <nav className="space-y-1">
          {sidebarLinks.map((link) => {
            const Icon = link.icon;
            const isActive = activeTab === link.value;
            return (
              <button
                key={link.value}
                type="button"
                onClick={() => setActiveTab(link.value)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                }`}
              >
                <Icon className="w-5 h-5 shrink-0" />
                {link.label}
              </button>
            );
          })}
        </nav>
        {activeCompany && (
          <div className="mt-6 pt-4 border-t border-gray-200">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
              Active Company
            </p>
            <div className="flex items-center gap-2 px-2">
              <CompanyAvatar
                company={activeCompany}
                size="xs"
                className="!w-5 !h-5"
              />
              <span className="text-sm font-medium text-gray-900 truncate">
                {activeCompany.name}
              </span>
            </div>
          </div>
        )}
      </aside>

      {/* ── Main content ── */}
      <div className="flex-1 min-w-0 p-4 overflow-y-auto">
        {activeTab === PROFILE_TABS.general && (
          <div className="space-y-6">
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                General Profile
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Manage your profile and account details
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
              <form onSubmit={formik.handleSubmit} className="p-6 sm:p-8">
                <div className="space-y-6">
                  <div className="p-4 rounded-xl bg-gray-50/80 border border-gray-100">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                      Email
                    </label>
                    <p className="text-base font-medium text-gray-900">
                      {user?.email}
                    </p>
                  </div>

                  <div>
                    <Input
                      label="Full Name"
                      name="name"
                      id="name"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="Enter your full name"
                      error={
                        formik.touched.name && formik.errors.name
                          ? formik.errors.name
                          : undefined
                      }
                    />
                  </div>
                </div>
                <div className="mt-8 pt-6 border-t border-gray-100">
                  <Button
                    type="submit"
                    fullWidth
                    loading={formik.isSubmitting}
                    disabled={formik.isSubmitting}
                  >
                    {formik.isSubmitting ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {activeTab === PROFILE_TABS.password && (
          <div className="space-y-6">
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                Update Password
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Change your account password
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
              <form
                onSubmit={passwordFormik.handleSubmit}
                className="p-6 sm:p-8 space-y-6"
              >
                <div>
                  <label
                    htmlFor="currentPassword"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Current Password
                  </label>
                  <PasswordInput
                    id="currentPassword"
                    name="currentPassword"
                    value={passwordFormik.values.currentPassword}
                    onChange={passwordFormik.handleChange}
                    onBlur={passwordFormik.handleBlur}
                    className={`${inputBase} ${
                      passwordFormik.touched.currentPassword &&
                      passwordFormik.errors.currentPassword
                        ? inputError
                        : ""
                    }`}
                    placeholder="Enter your current password"
                    error={
                      passwordFormik.touched.currentPassword &&
                      passwordFormik.errors.currentPassword
                        ? passwordFormik.errors.currentPassword
                        : undefined
                    }
                  />
                </div>

                <div>
                  <label
                    htmlFor="newPassword"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    New Password
                  </label>
                  <PasswordInput
                    id="newPassword"
                    name="newPassword"
                    value={passwordFormik.values.newPassword}
                    onChange={passwordFormik.handleChange}
                    onBlur={passwordFormik.handleBlur}
                    className={`${inputBase} ${
                      passwordFormik.touched.newPassword &&
                      passwordFormik.errors.newPassword
                        ? inputError
                        : ""
                    }`}
                    placeholder="Enter your new password"
                    error={
                      passwordFormik.touched.newPassword &&
                      passwordFormik.errors.newPassword
                        ? passwordFormik.errors.newPassword
                        : undefined
                    }
                  />
                </div>

                <div>
                  <label
                    htmlFor="confirmNewPassword"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Confirm New Password
                  </label>
                  <PasswordInput
                    id="confirmNewPassword"
                    name="confirmNewPassword"
                    value={passwordFormik.values.confirmNewPassword}
                    onChange={passwordFormik.handleChange}
                    onBlur={passwordFormik.handleBlur}
                    className={`${inputBase} ${
                      passwordFormik.touched.confirmNewPassword &&
                      passwordFormik.errors.confirmNewPassword
                        ? inputError
                        : ""
                    }`}
                    placeholder="Confirm your new password"
                    error={
                      passwordFormik.touched.confirmNewPassword &&
                      passwordFormik.errors.confirmNewPassword
                        ? passwordFormik.errors.confirmNewPassword
                        : undefined
                    }
                  />
                </div>

                <div className="pt-6 border-t border-gray-100">
                  <Button
                    type="submit"
                    variant="danger"
                    fullWidth
                    loading={passwordFormik.isSubmitting}
                    disabled={passwordFormik.isSubmitting}
                  >
                    {passwordFormik.isSubmitting
                      ? "Updating Password..."
                      : "Update Password"}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {activeTab === PROFILE_TABS.signature && (
          <div className="space-y-6">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Signature</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Create and manage your signature for documents
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
              <div className="p-6 sm:p-8">
                <Signature />
              </div>
            </div>
          </div>
        )}
      </div>

      <UpgradePlansModal
        isOpen={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        currentPlan={currentPlan}
        subscriptionType={subscriptionType}
        memberCount={memberCount}
        billingCycle={billingCycle}
        currentPrice={currentPrice}
        totalPrice={totalPrice}
      />
      <PaymentFailureModal
        isOpen={paymentFailureModalOpen}
        onClose={() => setPaymentFailureModalOpen(false)}
      />
    </div>
  );
};

export default Profile;
